/**
 * Поддержка экранной клавиатуры в PWA (главным образом iOS Safari).
 *
 * Проблема: на iOS клавиатура «накрывает» нижнюю часть экрана, и инпут,
 * на который тапнули, остаётся ЗА клавиатурой. Стандартный browser autoscroll
 * либо не работает, либо работает криво — особенно в кастомных модалах
 * (Sheet) с `transform: translateY` или внутри `position: fixed` контейнеров.
 *
 * Решение из двух частей:
 *
 *  1. Слушаем `window.visualViewport.resize` — это honest-API про реально
 *     видимую область. Когда появляется клавиатура, `visualViewport.height`
 *     уменьшается. Разница с обычным `innerHeight` = высота клавиатуры.
 *     Записываем её в CSS-переменную `--jb-kb-h` на `:root`. Sheet использует
 *     её, чтобы целиком подняться и уменьшить внутреннюю scroll-область, а
 *     отдельные кастомные оверлеи — чтобы остаться над клавиатурой.
 *
 *  2. На любой focus/click по input/textarea прокручиваем БЛИЖАЙШИЙ внутренний
 *     scroll-контейнер. Нельзя использовать обычный `scrollIntoView`: iOS
 *     способен прокрутить layout viewport целиком даже при `overflow:hidden`.
 *     Тогда весь `#root` остаётся сдвинутым вверх после закрытия клавиатуры,
 *     а fixed-панели висят отдельно над пустым фоном.
 *
 *  3. Layout viewport в приложении всегда должен оставаться в `(0, 0)`:
 *     страницы прокручиваются только во внутренних контейнерах. Поэтому
 *     исправляем любой системный iOS autoscroll при scroll/focusout и после
 *     закрытия клавиатуры.
 *
 * Вызывается один раз из main.tsx — глобальный effect на window.
 */

let installed = false;

type LayoutViewport = Pick<Window, 'scrollX' | 'scrollY'> & {
  scrollTo: (x: number, y: number) => void;
};

export function keyboardHeightFromViewport(
  layoutHeight: number,
  visualHeight: number,
  visualOffsetTop: number,
): number {
  return Math.max(0, Math.round(layoutHeight - visualHeight - visualOffsetTop));
}

/**
 * Стабильная высота, на которую должна уменьшаться геометрия fixed-sheet.
 *
 * visualViewport.offsetTop меняется, когда iOS панорамирует экран между
 * соседними полями. Для физического размера клавиатуры важна только потеря
 * высоты viewport; иначе вся шторка повторно прыгает на величину offsetTop.
 */
export function keyboardLayoutHeight(
  layoutHeight: number,
  visualHeight: number,
): number {
  return Math.max(0, Math.round(layoutHeight - visualHeight));
}

/**
 * Нужно ли перехватить нативный Safari focus-scroll при переходе между
 * editable-полями основной calc-form.
 *
 * Поля могут находиться в разных строках (например, «Комментарий → Высота»).
 * Пока клавиатура открыта, Safari способен сначала панорамировать весь visual
 * viewport, а уже затем вернуть fixed-overlay. Поэтому важны общая шторка и
 * общий внутренний scroll-контейнер, а не совпадение вертикальной координаты.
 */
export function shouldPreventNativeCalcFocusScroll(
  keyboardHeight: number,
  sameCalcForm: boolean,
  sameScrollContainer: boolean,
): boolean {
  return keyboardHeight >= 100 && sameCalcForm && sameScrollContainer;
}

/**
 * Первый тап по полю calc-form тоже должен обойти нативный focus-scroll iOS.
 *
 * Фокус ставится на pointerdown с preventScroll, но геометрия формы в этот
 * момент не меняется. Поэтому Safari не успевает панорамировать visual
 * viewport, а исходная цель тапа остаётся под пальцем до pointerup/click.
 */
export function shouldPreventNativeCalcInitialFocusScroll(
  keyboardHeight: number,
  insideCalcForm: boolean,
  hasScrollContainer: boolean,
): boolean {
  return keyboardHeight < 100 && insideCalcForm && hasScrollContainer;
}

/**
 * Возвращает внешний layout viewport в исходную позицию.
 *
 * Функция экспортируется отдельно, чтобы Sheet мог вызвать её после полного
 * закрытия последней шторки. Возвращаемое значение удобно для unit-теста и
 * диагностики: true означает, что смещение действительно было исправлено.
 */
export function resetLayoutViewportScroll(view?: LayoutViewport): boolean {
  const target = view ?? (typeof window !== 'undefined' ? window : undefined);
  if (!target) return false;
  if (Math.abs(target.scrollX) < 1 && Math.abs(target.scrollY) < 1) return false;
  target.scrollTo(0, 0);
  return true;
}

/**
 * iOS иногда применяет отложенный autoscroll уже после blur/unmount. Поэтому
 * одной синхронной коррекции недостаточно: повторяем её на animation frame и
 * после окончания системной анимации клавиатуры.
 */
export function scheduleLayoutViewportScrollReset(): void {
  if (typeof window === 'undefined') return;
  resetLayoutViewportScroll();
  window.requestAnimationFrame(() => resetLayoutViewportScroll());
  window.setTimeout(() => resetLayoutViewportScroll(), 350);
}

function isKeyboardEditable(element: HTMLElement): boolean {
  const tag = element.tagName;
  const isEditable =
    tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || element.isContentEditable;
  if (!isEditable) return false;

  if (tag === 'INPUT') {
    const type = (element as HTMLInputElement).type;
    if (['button', 'submit', 'checkbox', 'radio', 'file', 'reset', 'range', 'color'].includes(type)) {
      return false;
    }
  }
  return true;
}

function findScrollContainer(element: HTMLElement): HTMLElement | null {
  // Sheet всегда имеет собственную scroll-область, даже если прямо сейчас
  // контент в ней короче viewport.
  const sheetBody = element.closest<HTMLElement>('.jb-sheet__body');
  if (sheetBody) return sheetBody;

  let parent = element.parentElement;
  while (parent && parent !== document.body && parent !== document.documentElement) {
    const overflowY = window.getComputedStyle(parent).overflowY;
    if (/(auto|scroll|overlay)/.test(overflowY)) return parent;
    parent = parent.parentElement;
  }
  return null;
}

function centerInsideScrollContainer(
  element: HTMLElement,
  expectedKeyboardHeight?: number,
  onlyIfNeeded = false,
): void {
  const container = findScrollContainer(element);
  if (!container) return;

  const targetRect = element.getBoundingClientRect();
  const containerRect = container.getBoundingClientRect();
  const visualViewport = window.visualViewport;
  const viewportTop = visualViewport?.offsetTop ?? 0;
  // На pointerdown клавиатуры ещё физически нет, но ждать её финального
  // visualViewport.resize нельзя: пользователь успевает увидеть промежуточное
  // положение формы. Для этого первого движения используем ожидаемую нижнюю
  // границу будущего viewport. После открытия фактическая геометрия выполнит
  // лишь небольшую финальную коррекцию при необходимости.
  const viewportBottom = expectedKeyboardHeight === undefined
    ? viewportTop + (visualViewport?.height ?? window.innerHeight)
    : Math.max(viewportTop, window.innerHeight - expectedKeyboardHeight);
  // Контейнер fixed-sheet остаётся высотой с layout viewport, когда экранная
  // клавиатура уже уменьшила visual viewport. Центрируем по их пересечению,
  // иначе поле формально окажется «в центре» области, скрытой клавиатурой.
  const visibleTop = Math.max(containerRect.top, viewportTop);
  const visibleBottom = Math.min(containerRect.bottom, viewportBottom);
  // При переходе между соседними полями не двигаем форму повторно, если новое
  // поле уже полностью помещается в видимой области. Принудительное
  // центрирование каждого focusin давало заметный второй скачок даже для
  // полей «Ширина / Высота / Шт.», расположенных в одной строке.
  const visibilityMargin = Math.min(
    16,
    Math.max(0, (visibleBottom - visibleTop - targetRect.height) / 2),
  );
  if (
    onlyIfNeeded
    && targetRect.top >= visibleTop + visibilityMargin
    && targetRect.bottom <= visibleBottom - visibilityMargin
  ) {
    return;
  }
  const targetCenter = targetRect.top + targetRect.height / 2;
  const visibleCenter = visibleBottom > visibleTop
    ? visibleTop + (visibleBottom - visibleTop) / 2
    : containerRect.top + containerRect.height / 2;
  const maxScrollTop = Math.max(0, container.scrollHeight - container.clientHeight);
  const nextScrollTop = Math.max(
    0,
    Math.min(maxScrollTop, container.scrollTop + targetCenter - visibleCenter),
  );

  try {
    container.scrollTo({ top: nextScrollTop, behavior: 'smooth' });
  } catch {
    // Старый Safari не понимает объект ScrollToOptions.
    container.scrollTop = nextScrollTop;
  }
}

export function installKeyboardAware(): void {
  if (installed || typeof window === 'undefined') return;
  installed = true;

  // ─── 1. visualViewport → CSS variable ───
  const root = document.documentElement;
  let lastKeyboardHeight = 0;
  let rememberedKeyboardHeight = 0;
  let inputCenterTimer: number | null = null;
  let keyboardEditingExpiryTimer: number | null = null;
  let focusOutTimer: number | null = null;
  let focusSwitchScrollCleanup: (() => void) | null = null;
  let focusSwitchSnapshot: {
    container: HTMLElement;
    scrollTop: number;
    contentTop: number;
  } | null = null;

  const clearKeyboardEditingExpiry = () => {
    if (keyboardEditingExpiryTimer === null) return;
    window.clearTimeout(keyboardEditingExpiryTimer);
    keyboardEditingExpiryTimer = null;
  };

  const getExpectedKeyboardHeight = () => (
    rememberedKeyboardHeight > 0
      ? rememberedKeyboardHeight
      : Math.min(420, Math.max(260, Math.round(window.innerHeight * 0.42)))
  );

  /**
   * Фиксируем начало редактирования после завершённого click.
   *
   * Нельзя менять геометрию на pointerdown: поле успевает уехать из-под пальца
   * до pointerup, и итоговый click может попасть в расположенный выше picker.
   * После click цель нажатия уже зафиксирована, поэтому footer можно безопасно
   * скрыть до системного iOS autoscroll. Если экранная клавиатура не открылась
   * (например, подключена аппаратная), временный класс снимается автоматически.
   */
  const beginKeyboardEditing = () => {
    root.classList.add('jb-keyboard-editing');
    // Внешний layout viewport приложения остаётся в (0, 0). Поэтому до
    // первого visualViewport.resize заранее резервируем внутри локальной
    // scroll-области место под клавиатуру: поле успевает подняться одним
    // движением, а не остаётся под клавиатурой до финального resize Safari.
    root.style.setProperty(
      '--jb-kb-reserve-h',
      `${lastKeyboardHeight >= 100 ? lastKeyboardHeight : getExpectedKeyboardHeight()}px`,
    );
    clearKeyboardEditingExpiry();
    keyboardEditingExpiryTimer = window.setTimeout(() => {
      keyboardEditingExpiryTimer = null;
      if (lastKeyboardHeight < 100) {
        root.classList.remove('jb-keyboard-editing');
        root.style.setProperty('--jb-kb-reserve-h', '0px');
      }
    }, 700);
  };

  const centerInputForExpectedKeyboard = (element: HTMLElement) => {
    // После первого открытия используем реальную высоту клавиатуры устройства.
    // Для самого первого тапа берём консервативные 42% viewport — примерно
    // столько занимает цифровая клавиатура iPhone 14/16 вместе с accessory bar.
    centerInsideScrollContainer(element, getExpectedKeyboardHeight());
  };

  /**
   * iOS способен применить собственный scroll уже после focusin. Для полей,
   * стоящих в одной строке, на короткое время удерживаем исходный scrollTop:
   * новый input и так полностью виден, поэтому системный сдвиг только мигает
   * формой. При переходе decimal → numeric iOS применяет поздний автоскролл,
   * поэтому локальная блокировка живёт 650ms — до конца смены типа клавиатуры.
   */
  const preserveScrollDuringSameRowFocus = (
    container: HTMLElement,
    scrollTop: number,
    activeTarget: HTMLElement,
  ) => {
    focusSwitchScrollCleanup?.();
    let frame: number | null = null;
    let timer: number | null = null;
    let lateTimer: number | null = null;
    let cleanupTimer: number | null = null;

    const restore = () => {
      if (document.activeElement !== activeTarget) return;
      if (Math.abs(container.scrollTop - scrollTop) < 1) return;
      container.scrollTop = scrollTop;
    };
    const onScroll = () => restore();
    container.addEventListener('scroll', onScroll, { passive: true });
    restore();
    frame = window.requestAnimationFrame(restore);
    timer = window.setTimeout(restore, 80);
    lateTimer = window.setTimeout(restore, 420);

    const cleanup = () => {
      container.removeEventListener('scroll', onScroll);
      if (frame !== null) window.cancelAnimationFrame(frame);
      if (timer !== null) window.clearTimeout(timer);
      if (lateTimer !== null) window.clearTimeout(lateTimer);
      if (cleanupTimer !== null) window.clearTimeout(cleanupTimer);
      if (focusSwitchScrollCleanup === cleanup) {
        focusSwitchScrollCleanup = null;
      }
    };
    focusSwitchScrollCleanup = cleanup;
    cleanupTimer = window.setTimeout(cleanup, 650);
  };

  const centerActiveInputWhenKeyboardVisible = (onlyIfNeeded = false) => {
    if (lastKeyboardHeight < 100) return;
    const active = document.activeElement;
    if (!(active instanceof HTMLElement) || !isKeyboardEditable(active)) return;
    centerInsideScrollContainer(active, undefined, onlyIfNeeded);
  };

  /**
   * Один debounce вместо нескольких smooth-scroll подряд.
   *
   * visualViewport меняет высоту во время системной анимации клавиатуры.
   * Раньше контрольные замеры на 180/320/500ms каждый заново центрировали
   * поле — длинная форма визуально поднималась «этажами». Теперь любой новый
   * размер только переносит ОДИН финальный scroll на 80ms после последнего
   * фактического изменения геометрии.
   */
  const scheduleActiveInputCenter = (delay = 80, onlyIfNeeded = false) => {
    if (inputCenterTimer !== null) window.clearTimeout(inputCenterTimer);
    inputCenterTimer = window.setTimeout(() => {
      inputCenterTimer = null;
      centerActiveInputWhenKeyboardVisible(onlyIfNeeded);
      scheduleLayoutViewportScrollReset();
    }, delay);
  };

  const updateKbHeight = (): number => {
    const vv = window.visualViewport;
    if (!vv) {
      root.style.setProperty('--jb-kb-h', '0px');
      root.style.setProperty('--jb-kb-reserve-h', '0px');
      root.style.setProperty('--jb-vv-offset-top', '0px');
      root.classList.remove('jb-keyboard-open');
      lastKeyboardHeight = 0;
      return 0;
    }
    // Для layout sheet используем только потерю ВЫСОТЫ visual viewport.
    // offsetTop — панорамирование iOS к активному input, а не изменение
    // клавиатуры; его учёт заставлял всю шторку прыгать между соседними полями.
    const kb = keyboardLayoutHeight(window.innerHeight, vv.height);
    const visualOffsetTop = Math.max(0, Math.round(vv.offsetTop));
    const previousKeyboardHeight = lastKeyboardHeight;
    lastKeyboardHeight = kb;
    if (kb >= 100) {
      rememberedKeyboardHeight = Math.max(rememberedKeyboardHeight, kb);
    }
    root.style.setProperty('--jb-kb-h', `${kb}px`);
    root.style.setProperty('--jb-vv-offset-top', `${visualOffsetTop}px`);
    root.classList.toggle('jb-keyboard-open', kb >= 100);
    if (kb >= 100) {
      root.style.setProperty('--jb-kb-reserve-h', `${kb}px`);
      clearKeyboardEditingExpiry();
      root.classList.add('jb-keyboard-editing');
    }
    // Изменения в 1px бывают от округления Safari и не требуют нового layout.
    if (kb >= 100 && Math.abs(kb - previousKeyboardHeight) > 1) {
      scheduleActiveInputCenter();
    }
    // Стабилизируем внешний viewport только при РЕАЛЬНОМ закрытии клавиатуры,
    // а не на каждом раннем событии открытия, где Safari ещё сообщает kb=0.
    if (kb < 1 && previousKeyboardHeight >= 100) {
      clearKeyboardEditingExpiry();
      root.classList.remove('jb-keyboard-editing');
      root.style.setProperty('--jb-kb-reserve-h', '0px');
      scheduleLayoutViewportScrollReset();
    }
    return kb;
  };

  // Safari может прислать единственный visualViewport.resize в начале
  // системной анимации, когда height ещё содержит старое значение. Особенно
  // часто это происходит при повторном открытии клавиатуры: input остаётся
  // focused после кнопки Done, поэтому нового focusin тоже нет. Перемеряем
  // viewport несколькими короткими контрольными точками до конца анимации.
  let kbRefreshFrame: number | null = null;
  let kbRefreshTimers: number[] = [];
  const scheduleKbHeightUpdates = () => {
    if (kbRefreshFrame !== null) window.cancelAnimationFrame(kbRefreshFrame);
    kbRefreshTimers.forEach((timer) => window.clearTimeout(timer));
    kbRefreshTimers = [];

    updateKbHeight();
    kbRefreshFrame = window.requestAnimationFrame(() => {
      kbRefreshFrame = null;
      updateKbHeight();
    });
    kbRefreshTimers = [60, 180, 320, 500].map((delay) => window.setTimeout(() => {
      updateKbHeight();
    }, delay));
  };

  scheduleKbHeightUpdates();
  if (window.visualViewport) {
    window.visualViewport.addEventListener('resize', scheduleKbHeightUpdates);
    window.visualViewport.addEventListener('scroll', scheduleKbHeightUpdates);
  }
  window.addEventListener('resize', scheduleKbHeightUpdates);
  // В этой PWA document никогда не является scroll-контейнером: все экраны
  // прокручиваются внутри .jb-shell__main / локальных областей. Поэтому любое
  // window.scroll — побочный iOS autoscroll, который нужно сразу отменить.
  window.addEventListener('scroll', () => resetLayoutViewportScroll(), { passive: true });

  // ─── 2. Auto-scroll фокуса только внутри локальной scroll-области ───
  document.addEventListener('focusin', (e) => {
    const t = e.target as HTMLElement | null;
    if (!t || !isKeyboardEditable(t)) return;
    // Переход со старого editable-поля на новое — это не закрытие
    // клавиатуры. Отменяем отложенный cleanup старого focusout, чтобы он не
    // спорил с центрированием нового поля и не создавал мигание.
    if (focusOutTimer !== null) {
      window.clearTimeout(focusOutTimer);
      focusOutTimer = null;
    }
    let sameVisibleRow = false;
    const snapshot = focusSwitchSnapshot;
    focusSwitchSnapshot = null;
    if (snapshot && lastKeyboardHeight >= 100) {
      const nextContainer = findScrollContainer(t);
      if (nextContainer === snapshot.container) {
        const targetRect = t.getBoundingClientRect();
        const containerRect = nextContainer.getBoundingClientRect();
        const viewportTop = window.visualViewport?.offsetTop ?? 0;
        const viewportBottom = viewportTop
          + (window.visualViewport?.height ?? window.innerHeight);
        const visibleTop = Math.max(containerRect.top, viewportTop);
        const visibleBottom = Math.min(containerRect.bottom, viewportBottom);
        const nextContentTop = targetRect.top + nextContainer.scrollTop;
        const targetTopAtPreviousScroll = nextContentTop - snapshot.scrollTop;
        const targetBottomAtPreviousScroll = targetTopAtPreviousScroll + targetRect.height;
        sameVisibleRow = Math.abs(nextContentTop - snapshot.contentTop) < 12
          && targetTopAtPreviousScroll >= visibleTop + 16
          && targetBottomAtPreviousScroll <= visibleBottom - 16;
        if (sameVisibleRow) {
          preserveScrollDuringSameRowFocus(nextContainer, snapshot.scrollTop, t);
        }
      }
    }
    scheduleKbHeightUpdates();
    // Если клавиатура уже была открыта и пользователь перешёл к соседнему
    // полю, прокручиваем только если новое поле действительно вышло за
    // видимую область. Полностью видимое поле в той же строке не двигает форму.
    if (lastKeyboardHeight >= 100 && !sameVisibleRow) {
      scheduleActiveInputCenter(0, true);
    }
  });

  // Переводим фокус с preventScroll до нативного focus Safari:
  // - на первом тапе по editable-полю calc-form, пока клавиатура закрыта;
  // - при переходе между её полями, когда клавиатура уже открыта.
  //
  // Первый случай не даёт iOS панорамировать visual viewport на 200–260px и
  // затем кратко вернуть sheet через промежуточную высоту клавиатуры. Второй
  // охватывает как одну строку размеров, так и «Комментарий ↔ Высота».
  //
  // Геометрию/видимость footer на pointerdown по-прежнему не меняем. Ранний
  // focus сам по себе не сдвигает форму, поэтому исходный input остаётся целью
  // pointerup/click; layout начинает плавно перестраиваться только после click.
  document.addEventListener('pointerdown', (e) => {
    const t = e.target as HTMLElement | null;
    if (!t || !isKeyboardEditable(t)) return;
    const current = document.activeElement;
    const nextCalcForm = t.closest('.jb-sheet--calc-form');
    const nextContainer = findScrollContainer(t);
    if (
      shouldPreventNativeCalcInitialFocusScroll(
        lastKeyboardHeight,
        !!nextCalcForm,
        !!nextContainer,
      )
    ) {
      try {
        t.focus({ preventScroll: true });
      } catch {
        // Старый WebKit продолжит обычный нативный focus.
      }
    }
    if (
      lastKeyboardHeight >= 100
      && current instanceof HTMLElement
      && current !== t
      && isKeyboardEditable(current)
    ) {
      const currentCalcForm = current.closest('.jb-sheet--calc-form');
      const currentContainer = findScrollContainer(current);
      if (
        shouldPreventNativeCalcFocusScroll(
          lastKeyboardHeight,
          !!currentCalcForm && currentCalcForm === nextCalcForm,
          !!currentContainer && currentContainer === nextContainer,
        )
      ) {
        try {
          t.focus({ preventScroll: true });
        } catch {
          // Старый WebKit сам выполнит обычный focus после pointerdown.
        }
      }
    }
    scheduleKbHeightUpdates();
  }, { capture: true, passive: true });

  // После click цель нажатия уже зафиксирована. Теперь можно безопасно скрыть
  // footer и начать ОДНО плавное движение вместе с клавиатурой. Срабатывает и
  // для повторного тапа по уже focused input после системной кнопки Done.
  document.addEventListener('click', (e) => {
    const t = e.target as HTMLElement | null;
    if (!t || !isKeyboardEditable(t)) return;
    beginKeyboardEditing();
    if (
      lastKeyboardHeight < 100
      && (t.closest('.jb-sheet--calc-form') || t.closest('.jb-order-detail__scroll'))
    ) {
      centerInputForExpectedKeyboard(t);
    }
    scheduleKbHeightUpdates();
  }, { capture: true });

  document.addEventListener('focusout', (e) => {
    const t = e.target as HTMLElement | null;
    if (!t || !isKeyboardEditable(t)) return;
    focusSwitchScrollCleanup?.();
    const container = findScrollContainer(t);
    focusSwitchSnapshot = container
      ? {
          container,
          scrollTop: container.scrollTop,
          contentTop: t.getBoundingClientRect().top + container.scrollTop,
        }
      : null;
    if (focusOutTimer !== null) window.clearTimeout(focusOutTimer);
    focusOutTimer = window.setTimeout(() => {
      focusOutTimer = null;
      const nextActive = document.activeElement;
      // Между соседними input/textarea клавиатура остаётся открытой: геометрия
      // уже правильная, сбрасывать внешний viewport нельзя.
      if (nextActive instanceof HTMLElement && isKeyboardEditable(nextActive)) {
        return;
      }
      focusSwitchSnapshot = null;
      scheduleKbHeightUpdates();
      scheduleLayoutViewportScrollReset();
    }, 0);
  });
}
